import type { UserNoteEntityType } from '@shared/schema';
import { userNoteEntityResource } from '@shared/userNoteEntityResource';

export type NoteEntityContext = {
  entityType: UserNoteEntityType;
  entityId: number;
  route: string;
};

export type QuickNoteFileSource = {
  id: number;
  name: string;
  targets: Array<NoteEntityContext & { label: string }>;
};

export type QuickNoteComposerDetail = Partial<NoteEntityContext> & {
  startVoice?: boolean;
  sourceFile?: QuickNoteFileSource;
  historyText?: string;
};

export const NOTE_ENTITY_TYPES: NoteEntityContext['entityType'][] = ['contact', 'company', 'lead', 'deal', 'branch'];

export function validNoteEntityContext(value: Partial<NoteEntityContext> | null | undefined): NoteEntityContext | null {
  if (!value || !NOTE_ENTITY_TYPES.includes(value.entityType as NoteEntityContext['entityType'])) return null;
  const entityId = Number(value.entityId);
  if (!Number.isSafeInteger(entityId) || entityId <= 0) return null;
  const entityType = value.entityType as NoteEntityContext['entityType'];
  const plural = userNoteEntityResource(entityType);
  return { entityType, entityId, route: `/${plural}/${entityId}` };
}

export function quickNoteText(draft: string, sourceFile?: QuickNoteFileSource | null): string {
  const text = draft.trim();
  if (!text || !sourceFile) return text;
  return `${text}\n\nDocumento CRM di origine: ${sourceFile.name} (/files?fileId=${sourceFile.id})`;
}

const ENTITY_PATHS: Record<string, UserNoteEntityType> = {
  contacts: 'contact',
  companies: 'company',
  deals: 'deal',
  leads: 'lead',
  branches: 'branch',
};

/**
 * Resolve the current CRM record from the browser route so Note rapide can
 * append into the same canonical notes_user boundary used by entity panels.
 */
export function resolveNoteEntityContextFromPath(pathname: string): NoteEntityContext | null {
  const match = pathname.match(/^\/(contacts|companies|deals|leads|branches)\/(\d+)(?:\/|$|\?)/);
  if (!match) return null;
  const entityType = ENTITY_PATHS[match[1]];
  const entityId = Number.parseInt(match[2], 10);
  if (!entityType || !Number.isSafeInteger(entityId) || entityId <= 0) return null;
  return {
    entityType,
    entityId,
    route: `/${match[1]}/${entityId}`,
  };
}

export const OPEN_QUICK_NOTE_EVENT = 'openQuickNoteComposer';

export function openQuickNoteComposer(detail?: QuickNoteComposerDetail): void {
  document.dispatchEvent(new CustomEvent(OPEN_QUICK_NOTE_EVENT, { detail: detail ?? null }));
}
